import  { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Toast } from "../../../Common/Toast-Snackbar/ToastSnackbar";
import { post } from "../../../helper/Api_helper";
import {
  VYLABS_BASE_URL,
  SEARC_JOBS,
  JOB_BLAST_FOR_CANDIDATES,
  JOB_BLAST_FOR_CANDIDATES_PREVIEW,
} from "../../../helper/url_helper";
import {
  toCapitalize,
} from "../../../Common/Common-Function/CommonFunctions";
import AutoComplete from "../../../Common/AutoComplete/AutoComplete";
import DynamicAutocomplete from "../../../Common/AutoSuggestion/DynamicAutocomplete";
import TextInput from "../../../Common/Textfield/Textfield";
import TextArea from "../../../Common/TextArea/TextArea";
import Button from "../../../Common/Button/Button";
import SendJobBlastIcon from "../../../Assets/Images/sendJobBlast.svg";
import { StandardPopup } from "../../../Common/PopupStyles/PopupComponent";
import CustomTooltip from "../../../Common/Tooltip/Tooltip";
import EmailPreviewPopup from "../../../Common/EmailPreviewPopup/EmailPreviewPopup";

const JobsBlastEmailCampaign = ({
  selectedCandidates,
  isEmailPreviewOpen,
  setIsEmailPreviewOpen,
  onCancel = () => {},
  setCanPreview,
  jobId,
  jobTitle,
}) => {
  const [isSending, setIsSending] = useState(false);
  const [jobTouched, setJobTouched] = useState(false);
  const [jobError, setJobError] = useState("");
  const [recipientsTouched, setRecipientsTouched] = useState(false);
  const [recipientsError, setRecipientsError] = useState("");
  const [previewData, setPreviewData] = useState({
    body: "",
    subject: "",
    isLoading: false,
  });
  const jobBlastFormik = useFormik({
    initialValues: {
      recipients: selectedCandidates.map((c) => c.name),
      selectedJob: null,
      subject: "",
      message: ``,
    },
    validationSchema: Yup.object({
      recipients: Yup.array()
        .min(1, "At least one recipient is required")
        .required("Recipients are required"),
      selectedJob: Yup.object().nullable(),
      subject: Yup.string().required("Subject is required"),
      message: Yup.string().required("Message is required"),
    }),
    onSubmit: async (values) => {
      const filteredCandidates = selectedCandidates.filter((c) =>
        values.recipients.includes(c.name),
      );

      if (!jobBlastFormik.values.selectedJob?.value && !jobId) {
        setJobTouched(true);
        setJobError("Job is required");
        Toast("error", "Please select a job.");
        return;
      }

      if (filteredCandidates.length === 0) {
        Toast("error", "No candidates selected to send email");
        return;
      }
      setIsSending(true);
      try {
        const payload = {
          candidate_ids: filteredCandidates.map((c) => c.id),
          job_id: values.selectedJob?.value ? values.selectedJob?.value : jobId,
          message: values.message,
          subject: values.subject,
        };
         await post(
          VYLABS_BASE_URL,
          JOB_BLAST_FOR_CANDIDATES,
          payload,
        );
        Toast(
          "success",
          `Email campaign sent to ${filteredCandidates.length} candidates`,
        );
        onCancel();
      } catch (error) {
        console.error("Error sending email campaign:", error);
        Toast(
          "error",
          error.message || "Failed to send email campaign. Please try again.",
        );
      } finally {
        setIsSending(false);
      }
    },
  });

  const accessJobBlastPreview = () => {
    const { recipients, selectedJob, subject, message } = jobBlastFormik.values;

    const hasRecipients = Array.isArray(recipients) && recipients.length > 0;

    const hasJob = selectedJob !== null && selectedJob !== undefined || jobId;

    const hasSubject = typeof subject === "string" && subject.trim().length > 0;

    const hasMessage = typeof message === "string" && message.trim().length > 0;

    return hasRecipients && hasJob && hasSubject && hasMessage;
  };

  const canSendJobBlast = accessJobBlastPreview();

  useEffect(() => {
    if (setCanPreview) {
      setCanPreview(accessJobBlastPreview());
    }
  }, [jobBlastFormik.values, setCanPreview]);

  const [jobListOptions, setJobListOptions] = useState([]);
  const [jobOptionLoading, setJobOptionLoading] = useState(false);
  const messageWrapperRef = useRef(null);

  useEffect(() => {
    const textarea = messageWrapperRef.current?.querySelector("textarea");
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [jobBlastFormik.values.message]);

  useEffect(() => {
    if (jobTouched) {
      setJobError(
        jobBlastFormik.values.selectedJob &&
          jobBlastFormik.values.selectedJob.value
          ? ""
          : "Job is required",
      );
    }
  }, [jobBlastFormik.values.selectedJob, jobTouched]);

  useEffect(() => {
    const recipients = jobBlastFormik.values.recipients;
    if (recipientsTouched) {
      if (!recipients || recipients.length === 0) {
        setRecipientsError("Recipients are required");
      } else {
        setRecipientsError("");
      }
    }
  }, [jobBlastFormik.values.recipients, recipientsTouched]);

  const handleJobInputChange = async (_, value) => {
    if (value && value.length >= 3) {
      fetchJobOptions(value);
    } else if (!value) {
      setJobListOptions([]);
    }
  };

  const fetchJobOptions = async (inputValue) => {
    if (!inputValue) return;
    setJobOptionLoading(true);
    try {
      const response = await post(VYLABS_BASE_URL, SEARC_JOBS, {
        query: inputValue,
      });
      const data = response.jobs || [];
      const fetchOptions = [
        ...new Map(
          data.map((item) => [item.id, { label: item.title, value: item.id }]),
        ).values(),
      ];
      setJobListOptions(fetchOptions);
    } catch (error) {
      console.error("Error fetching jobs:", error);
    } finally {
      setJobOptionLoading(false);
    }
  };

  const fetchEmailPreview = async () => {
    const { selectedJob, subject, message } = jobBlastFormik.values;
    setPreviewData((prev) => ({ ...prev, isLoading: true }));
    try {
      const payload = {
        job_id: selectedJob?.value ? selectedJob?.value : jobId,
        message: message,
        subject: subject,
      };

      const response = await post(
        VYLABS_BASE_URL,
        JOB_BLAST_FOR_CANDIDATES_PREVIEW,
        payload,
      );

      setPreviewData({
        body: response.body || "",
        subject: response.subject || subject,
        isLoading: false,
      });
      setIsEmailPreviewOpen(true);
    } catch (error) {
      console.error("Error fetching email preview:", error);
      Toast(
        "error",
        error.message || "Failed to generate email preview. Please try again.",
      );
      setPreviewData((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const handleEmailPreviewClick = async () => {
    if (!canSendJobBlast) {
      return;
    }
    await fetchEmailPreview();
  };
  const isDisabled = isSending || !canSendJobBlast;
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "18px",
        backgroundColor: "var(--body__background)",
      }}
    >
      <div>
        <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span>
            Recipients <span className="required-asterisk">*</span>
          </span>
        </label>
        <AutoComplete
          tags
          placeholder="Select candidates"
          value={jobBlastFormik.values.recipients.map((name) =>
            name,
          )}
          onChange={(val) => {
            jobBlastFormik.setFieldValue("recipients", val);
            setRecipientsTouched(true);
          }}
          containerStyle={{
            width: "100%",
            marginTop: "10px",
            marginBottom: "5px",
          }}
          InputWrapperStyle={{
            maxHeight: "80px",
            overflowY: "auto",
            border: "1px solid var(--border__color)",
            padding: "8px",
            borderRadius: "12px",
          }}
          inputReadOnly={true}
          useToCapitalize={true}
        />
        {recipientsTouched && recipientsError && (
          <div
            style={{
              color: "red",
              fontSize: "16px",
              marginBottom: "6px",
              textAlign: "left",
            }}
          >
            {recipientsError}
          </div>
        )}

        <span
          style={{ fontSize: "12px", color: "#6B7280", fontStyle: "italic" }}
        >
          Emails will be sent separately to each candidate
        </span>
      </div>
      { !jobId ? (
      <div>
        <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span>
            Select Job <span style={{ color: "red" }}>*</span>
          </span>
        </label>
        <DynamicAutocomplete
          name="selectedJob"
          placeholder="Search jobs..."
          noOptionsText="No jobs found."
          minCharsForSearch={3}
          options={jobListOptions}
          value={jobBlastFormik.values.selectedJob}
          onChange={(value) => {
            jobBlastFormik.setFieldValue("selectedJob", value);
            setJobTouched(true);
          }}
          onInputChange={handleJobInputChange}
          loading={jobOptionLoading}
          onBlur={() => {
            setJobTouched(true);
          }}
          error={jobTouched && Boolean(jobError)}
          sx={{
            mt: "5px",
            ".MuiOutlinedInput-root": {
              borderRadius: "12px",
              height: "40px",
              "& .MuiOutlinedInput-notchedOutline": {
                border: "1px solid var(--border__color) !important",
                borderRadius: "12px",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#6860ee",
                borderRadius: "12px",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#7a70f2",
                borderRadius: "12px",
              },
            },
            ".MuiInputBase-input": {
              color: "var(--header__color)",
              padding: "8px 12px !important",
              height: "40px",
              fontFamily: "Satoshi !important",
            },
          }}
          slotProps={{
            paper: {
              sx: {
                backgroundColor: "var(--body__background)",
                color: "var(--header__color)",
                borderRadius: "12px",
                boxShadow: "0px 4px 20px rgba(0,0,0,0.1)",
                border: "1px solid var(--header__color)",
              },
            },
            listbox: {
              sx: {
                "& li": {
                  fontSize: "14px",
                  fontWeight: 500,
                  padding: "8px 16px",
                  color: "var(--header__color)",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.04)",
                  },
                },
              },
            },
          }}
        />
        {jobTouched && jobError && (
          <div
            style={{
              color: "red",
              fontSize: "16px",
              marginBottom: "6px",
              textAlign: "left",
            }}
          >
            {jobError}
          </div>
        )}
        <span
          style={{ fontSize: "12px", color: "#6B7280", fontStyle: "italic" }}
        >
          An invitation to apply to this job will be included in the email
        </span>
      </div>
      ):(
        <div>
            <TextInput
              label="Job"
              labelStyle={{ fontWeight: "400" }}
              readOnly
              value={jobTitle}
              onChange={() => {}}
              required
            />
        <span
          style={{ fontSize: "12px", color: "#6B7280", fontStyle: "italic" }}
        >
          An invitation to apply to this job will be included in the email
        </span>
      </div>
      )}
      <div>
        <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span>
            Subject <span style={{ color: "red" }}>*</span>
          </span>
        </label>
        <TextInput
          name="subject"
          placeholder="e.g. Job Opportunity"
          value={jobBlastFormik.values.subject}
          onChange={jobBlastFormik.handleChange}
          onBlur={jobBlastFormik.handleBlur}
          error={
            jobBlastFormik.touched.subject &&
            Boolean(jobBlastFormik.errors.subject)
          }
          errorMessage={jobBlastFormik.errors.subject}
          style={{ border: "1px solid var(--border__color)" }}
        />

        <div style={{ marginTop: "14px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "5px",
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginTop: "5px",
              }}
            >
              <span>
                Message <span style={{ color: "red" }}>*</span>
              </span>
            </label>
          </div>
          <div ref={messageWrapperRef}>
            <TextArea
              rows={6}
              name="message"
              placeholder="Enter the candidate email message "
              value={jobBlastFormik.values.message}
              onChange={jobBlastFormik.handleChange}
              onBlur={jobBlastFormik.handleBlur}
              style={{
                border: "1px solid var(--border__color)",
                overflow: "hidden",
                resize: "none",
              }}
            />
          </div>
          {jobBlastFormik.touched.message && jobBlastFormik.errors.message && (
            <div
              style={{
                color: "red",
                fontSize: "12px",
                marginTop: "4px",
                marginLeft: "14px",
              }}
            >
              {jobBlastFormik.errors.message}
            </div>
          )}
        </div>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "14px",
          marginTop: "20px",
        }}
      >
        <Button
          style={{ marginRight: "0px" }}
          label="Cancel"
          isCancel
          event={onCancel}
        />
        <CustomTooltip
          title={
            canSendJobBlast
              ? "Click to view preview"
              : "Please fill all required fields to view preview"
          }
          arrow
        >
          <span>
            <Button
              label={previewData.isLoading ? "Generating..." : "Email Preview"}
              event={handleEmailPreviewClick}
              backgroundColor="#6860ee"
              color="#FFFFFF"
              disabled={!canSendJobBlast || previewData.isLoading}
              style={{ height: "42px" }}
              {...((!canSendJobBlast || previewData.isLoading) && {
                hoverd: false,
                hoverStyles: {},
                hoverIconSrc: null,
                style: {
                  backgroundColor: "var(--disabled-button)",
                  color: "white",
                  cursor: "not-allowed",
                  opacity: 0.7,
                  height: "42px",
                },
              })}
            />
          </span>
        </CustomTooltip>
        <Button
          label={
            isSending
              ? "Sending Email..."
              : `Send to ${jobBlastFormik.values.recipients.length} ${
                  jobBlastFormik.values.recipients.length === 1
                    ? "Candidate"
                    : "Candidates"
                }`
          }
          iconSrc={SendJobBlastIcon}
          event={jobBlastFormik.handleSubmit}
          disabled={isDisabled}
          {...(isDisabled && {
            hoverd: false,
            hoverStyles: {},
            hoverIconSrc: null,
            style: {
              backgroundColor: "var(--disabled-button)",
              color: "white",
              cursor: "disabled",
              opacity: 0.7,
            },
          })}
        />
      </div>
      {ReactDOM.createPortal(
        <StandardPopup
          isOpen={isEmailPreviewOpen}
          onClose={() => setIsEmailPreviewOpen(false)}
          title="Email Preview"
          hideActions={true}
          width="900px"
          maxHeight="95vh"
          popupStyle={{
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
          contentStyle={{
            padding: 0,
            marginTop: 0,
          }}
        >
          <div style={{ overflow: "hidden" }}>
            <EmailPreviewPopup htmlContent={previewData.body} />
          </div>
        </StandardPopup>,
        document.body,
      )}
    </div>
  );
};

export default JobsBlastEmailCampaign;